<template>
  <v-dialog
    :value="visible"
    @input="$emit('close')"
    max-width="900"
    persistent
  >
    <v-card>
        <v-toolbar dark color="primary">
          <v-toolbar-title>
            Asociar Cobro a Presupuesto - {{ pacienteNombre }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="$emit('close')">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-4">
          <v-alert
            v-if="idPresupuestoActual"
            type="info"
            dense
            text
            class="mb-3"
          >
            <template v-if="idPresupuestoDirecto">
              Este cobro ya está asociado directamente al presupuesto
              <strong>#{{ idPresupuestoActual }}</strong>. Si elige otro, se
              reasignará al presupuesto seleccionado.
            </template>
            <template v-else>
              Este cobro queda asociado al presupuesto
              <strong>#{{ idPresupuestoActual }}</strong> porque su turno está
              vinculado a ese presupuesto. Si elige uno, se imputará el cobro
              directamente al presupuesto seleccionado.
            </template>
          </v-alert>

          <v-chip label color="transparent" text-color="black">
            <h3>Presupuestos del Paciente</h3>
          </v-chip>

          <v-data-table
            v-if="listaPresupuestos.length"
            :headers="headers"
            :items="listaPresupuestos"
            class="elevation-1 mt-2"
            locale="es-ar"
            item-key="idPresupuesto"
            :items-per-page="5"
            :disable-sort="true"
            :item-class="rowClasses"
            :footer-props="{ itemsPerPageText: 'Filas por página' }"
          >
            <template v-slot:item.actions="{ item }">
              <v-chip
                v-if="esAsociadoActual(item)"
                small
                color="info"
                class="mr-2"
                outlined
              >
                Asociado
              </v-chip>
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    medium
                    color="green"
                    v-on="on"
                    v-bind="attrs"
                    @click.stop="asociar(item)"
                    >mdi-link</v-icon
                  >
                </template>
                <span>
                  {{
                    esAsociadoActual(item)
                      ? "Reasociar a este Presupuesto"
                      : "Asociar a este Presupuesto"
                  }}
                </span>
              </v-tooltip>
            </template>
          </v-data-table>

          <v-alert v-else class="mt-2" color="purple" dark outlined>
            El paciente no tiene presupuestos registrados.
          </v-alert>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="error" @click.stop="$emit('close')">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
  </v-dialog>
</template>

<script>
import axios from "../../axios-auth.js";

export default {
  props: {
    visible: { type: Boolean, default: false },
    // Los ids se tipan laxos: desde el flujo del turno pueden llegar como number,
    // string o null según el origen del cobro. Se normalizan al usarlos.
    idCobro: { default: 0 },
    idPaciente: { default: 0 },
    pacienteNombre: { type: String, default: "" },
    // Presupuesto EFECTIVO asociado al cobro (directo o vía turno). Se usa para
    // resaltar/marcar el presupuesto ya asociado en la lista.
    idPresupuestoActual: { default: null },
    // Presupuesto imputado DIRECTAMENTE al cobro (puede ser null si la asociación
    // proviene del turno). Se usa para matizar el texto informativo.
    idPresupuestoDirecto: { default: null },
  },
  data: () => ({
    listaPresupuestos: [],
    headers: [
      { text: "Fecha Presupuesto", value: "fechaPresupuestoString" },
      { text: "Profesional", value: "nombreProfesional" },
      { text: "Importe Total", value: "totalString" },
      { text: "Pagado", value: "pagadoString" },
      { text: "Saldo", value: "saldoString" },
      { text: "Acciones", value: "actions" },
    ],
  }),
  watch: {
    visible(nuevoValor) {
      if (nuevoValor && this.idPaciente) {
        this.buscarPresupuestos();
      }
    },
  },
  methods: {
    esAsociadoActual(item) {
      return (
        this.idPresupuestoActual &&
        item.idPresupuesto === this.idPresupuestoActual
      );
    },
    rowClasses(item) {
      return this.esAsociadoActual(item) ? "fila-asociada" : "";
    },
    showAlert(alertInfo) {
      this.$swal.fire({
        icon: alertInfo.icon,
        title: alertInfo.title,
        showConfirmButton: false,
        background: "#ececec",
        timer: alertInfo.timer,
      });
    },
    buscarPresupuestos() {
      let self = this;
      axios
        .get("/Caja/BuscarPresupuestosPorPaciente?", {
          params: {
            idPaciente: this.idPaciente,
            idInstitucion:
              this.$store.state.usuario.institucion.idInstitucion,
          },
        })
        .then((response) => {
          self.listaPresupuestos =
            response.data != null && response.data != "" ? response.data : [];
        })
        .catch(() => {
          self.listaPresupuestos = [];
        });
    },
    asociar(item) {
      this.$swal({
        title: "Asociar Cobro",
        text: "¿Desea asociar este cobro al presupuesto seleccionado?",
        icon: "success",
        background: "#ececec",
        showClass: { popup: "animate__animated animate__fadeInDown" },
        hideClass: { popup: "animate__animated animate__fadeOutUp" },
        showCancelButton: true,
        confirmButtonText: "Si",
        cancelButtonText: "No",
        showCloseButton: true,
        showLoaderOnConfirm: true,
      }).then((result) => {
        if (result.value) {
          let self = this;
          axios
            .post("/Caja/AsociarCobroPresupuesto", {
              idCobro: self.idCobro,
              idPresupuesto: item.idPresupuesto,
            })
            .then((response) => {
              if (response.data && response.data.resultado === 1) {
                self.showAlert({
                  icon: "success",
                  title: "Cobro asociado al presupuesto correctamente.",
                  timer: 2200,
                });
                self.$emit("asociado");
                self.$emit("close");
              } else {
                self.showAlert({
                  icon: "error",
                  title:
                    (response.data && response.data.mensaje) ||
                    "El cobro no pudo ser asociado.",
                  timer: 2800,
                });
              }
            })
            .catch(() => {
              self.showAlert({
                icon: "error",
                title: "El cobro no pudo ser asociado.",
                timer: 2800,
              });
            });
        }
      });
    },
  },
};
</script>

<style scoped>
::v-deep .fila-asociada {
  background-color: #e3f2fd;
}
</style>
